import { DataTable } from "./DataTable/DataTable";
import { Plus, Edit3, Trash2, Star, Eye, Search } from "lucide-react";
import { useState, useEffect } from "react";
import { Clinic } from "../types/clinic";
import { AddNewClinicModal } from "../shared/AddNewClinic";
import { ClinicService } from "../services/api/clinic.service";
import toast from "react-hot-toast";
import clinicPlaceholder from "../assets/images/default_avatar.jpg";
import { ClinicDetails } from "./clinic/ClinicDetails";
import { useAuth } from "../context/AuthContext";

export function ClinicsView() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [ShowAddClinicModal, setShowAddClinicModal] = useState(false);
  const [clinicList, setClinicList] = useState<Clinic[]>([]);
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [showClinicDetails, setShowClinicDetails] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [previousPage, setPreviousPage] = useState<number | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading clinics:", error);
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const loadClinics = async (page = 1, pageSize = 10, searchText = "") => {
    try {
      setIsLoading(true);
      const response = await ClinicService.getClinics({
        page,
        pageSize,
        searchText,
      });
      if (response.success && response.data?.dataList) {
        setClinicList(response.data.dataList);
        setCurrentPage(response.data.currentPage);
        setTotalPages(response.data.totalPages);
        setNextPage(response.data.nextPage);
        setPreviousPage(response.data.previousPage);
      } else {
        toast.error(response.message || "No clinics found");
      }
    } catch (error) {
      console.error("Error loading clinics:", error);
      toast.error("Failed to load clinics");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    loadClinics(currentPage, pageSize, searchText ?? "");
  }, [currentPage, pageSize, searchText]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleEditClick = (clinic: Clinic) => {
    setSelectedClinic(clinic);
    setShowAddClinicModal(true);
  };

  const columns = [
    {
      key: "name",
      title: "Clinic Name",
      sortable: true,
      width: "w-[200px]",
      render: (clinic: Clinic) => (
        <div className="flex items-center">
          <img
            src={
              typeof clinic.profileImage === "string"
                ? clinic.profileImage
                : clinicPlaceholder
            }
            alt={clinic.name}
            className="w-8 h-8 rounded-full mr-3 object-cover border border-gray-200"
          />
          <div>
            <div className="font-medium text-gray-900">{clinic.name}</div>
          </div>
        </div>
      ),
    },
    {
      key: "contact",
      title: "Contact",
      width: "w-[200px]",
      render: (clinic: Clinic) => (
        <div>
          <div className="text-sm">{clinic.mobileNumber}</div>
        </div>
      ),
    },
    {
      key: "address",
      title: "Address",
      sortable: true,
      width: "w-[150px]",
      render: (clinic: Clinic) => (
        <div className="text-sm font-medium text-justify">
          {clinic.address || "no address"} , {clinic.city} - {clinic.pincode} ,{" "}
          {clinic.state}
        </div>
      ),
    },
    {
      key: "supervisor",
      title: "Supervisor",
      sortable: true,
      width: "w-[150px]",
      render: (clinic: Clinic) => (
        <div className="text-sm font-medium">
          {clinic.supervisorName || "N/A"}
        </div>
      ),
    },
    {
      key: "rating",
      title: "Rating",
      width: "w-[150px]",
      render: (clinic: Clinic) => (
        <div className="flex items-center space-x-1">
          <span className="text-sm">{clinic.avgRating}</span>
          <Star
            className="w-5 h-5"
            style={{ fill: "#FFEB3C", color: "#FFEB3C" }}
          />
        </div>
      ),
    },
    {
      key: "registration_number",
      title: "Registration Number",
      sortable: true,
      width: "w-[120px]",
      render: (clinic: Clinic) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium `}
        >
          {clinic.registrationNumber || "N/A"}
        </span>
      ),
    },
    {
      key: "drug_License_Number",
      title: "Drug License Number",
      width: "w-[150px]",
      render: (clinic: Clinic) => (
        <div className="text-sm">{clinic.drugLicenseNumber || "N/A"}</div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      width: "w-[200px]",
      render: (clinic: Clinic) => (
        <div className="flex items-center gap-4">
          {/* Edit */}
          <button
            className="text-green-600 hover:text-green-700"
            onClick={() => handleEditClick(clinic)}
          >
            <Edit3 className="w-5 h-5" />
          </button>

          <button
            className="text-green-600 hover:text-green-700"
            onClick={() => {
              setShowClinicDetails(true);
            }}
          >
            <Eye className="w-5 h-5" />
          </button>
          {/* Delete */}
          {/* <button
            className="text-red-600 hover:text-red-700"
            onClick={() => handleClinicDelete(clinic.id)}
          >
            <Trash2 className="w-5 h-5" />
          </button> */}
        </div>
      ),
    },
  ];

  const addClinicButton = (
    <button
      onClick={() => setShowAddClinicModal(true)}
      className="relative z-10 flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
    >
      <Plus className="w-5 h-5 mr-2" />
      Add Clinic
    </button>
  );

  const handleAddClinicSuccess = () => {
    setShowAddClinicModal(false);
    loadClinics();
  };

  const handleClinicDelete = async (id: string) => {
    try {
      const response = await ClinicService.deleteExistingClinic(id);

      if (response.success) {
        toast.success("Clinic deleted successfully!");
        loadClinics();
      } else {
        toast.error(response.message || "Failed to delete Clinic.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }
  };
  return (
    <div className="relative pb-2">
      {!showClinicDetails && (
        <div
          className={`flex justify-end ${
            user?.role.toLocaleLowerCase() === "admin"
              ? "relative top-9"
              : user?.role.toLocaleLowerCase() === "clinic"
              ? "top-6"
              : null
          }`}
        >
          <div>
            <Search className="relative top-[2.25rem] left-4 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search clinic..."
              value={searchText}
              onChange={handleSearchChange}
              className="border rounded-lg px-3 py-2.5 text-sm w-64 text-center"
            />
          </div>
        </div>
      )}
      {showClinicDetails && selectedClinic ? (
        <ClinicDetails
          onClose={() => {
            setShowClinicDetails(false);
            setSelectedClinic(null);
          }}
          clinic={selectedClinic}
          onEdit={handleEditClick}
        />
      ) : (
        <DataTable
          data={clinicList}
          columns={columns}
          itemsPerPage={pageSize}
          searchable
          searchFields={[
            "name",
            "region",
            "supervisors",
            "supervisorName",
            "email",
            "mobileNumber",
            "address",
            "avgRating",
            "drugLicenseNumber",
            "registrationNumber",
            "state",
            "city",
            "pincode",
          ]}
          isLoading={isLoading}
          onRowClick={(clinic) => {
            setSelectedClinic(clinic);
            setShowClinicDetails(true);
          }}
          customFilters={addClinicButton}
          pagination={{
            currentPage,
            setCurrentPage,
            setTotalPages,
            totalPages,
            pageSize,
            setPageSize,
          }}
        />
      )}
      {ShowAddClinicModal && (
        <AddNewClinicModal
          onClose={() => {
            setShowAddClinicModal(false);
            setSelectedClinic(null);
          }}
          onSuccess={handleAddClinicSuccess}
          initialData={selectedClinic}
        />
      )}
    </div>
  );
}
